import { keepPreviousData } from '@tanstack/react-query';
import { trpc } from '@/utils/trpc';

/**
 * Analytics filter options shared across all hooks
 */
export interface InsightFilterOptions {
  tenantId?: string;
  domainId?: string;
  subjectId?: string;
  surveyId?: string;
  dateRange?: {
    start: string; // ISO 8601 datetime
    end: string; // ISO 8601 datetime
  };
  granularity?: 'day' | 'week' | 'month';
}

/**
 * Helper function to determine if query should retry
 * Don't retry on 4xx client errors, but retry on server errors
 */
function shouldRetryError(failureCount: number, error: unknown): boolean {
  const httpStatus =
    error && typeof error === 'object' && 'data' in error
      ? (error.data as { httpStatus?: number } | null | undefined)?.httpStatus
      : undefined;

  // Don't retry on 4xx errors
  if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
    return false;
  }

  // Retry up to 3 times for other errors
  return failureCount < 3;
}

/**
 * Hook to fetch analytics summary statistics
 *
 * Returns summary counts for domains, subjects, surveys, instances,
 * responses, and completion metrics.
 *
 * @param filters - Analytics filter options
 * @param enabled - Whether query is enabled (default: true)
 */
export const useInsightSummary = (filters: InsightFilterOptions, enabled: boolean = true) => {
  return trpc.insight.getSummary.useQuery(filters, {
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - summary changes slowly
    refetchOnWindowFocus: false, // Don't refetch on window focus for analytics
    retry: shouldRetryError,
  });
};

/**
 * Hook to fetch time series analytics data
 *
 * Returns array of time series data points with response counts
 * and active survey counts over time.
 *
 * @param filters - Analytics filter options
 * @param enabled - Whether query is enabled (default: true)
 */
export const useInsightTimeSeries = (
  filters: InsightFilterOptions,
  enabled: boolean = true
) => {
  return trpc.insight.getTimeSeries.useQuery(filters, {
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: shouldRetryError,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch domain breakdown analytics
 *
 * Returns array of domain statistics with subject, survey,
 * and response counts per domain.
 *
 * @param filters - Analytics filter options
 * @param enabled - Whether query is enabled (default: true)
 */
export const useInsightDomainBreakdown = (
  filters: InsightFilterOptions,
  enabled: boolean = true
) => {
  return trpc.insight.getDomainBreakdown.useQuery(filters, {
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: shouldRetryError,
  });
};

/**
 * Hook to fetch subject breakdown analytics
 *
 * Returns array of subject statistics with survey, response counts,
 * and completion rates per subject.
 *
 * @param filters - Analytics filter options
 * @param enabled - Whether query is enabled (default: true)
 */
export const useInsightSubjectBreakdown = (
  filters: InsightFilterOptions,
  enabled: boolean = true
) => {
  return trpc.insight.getSubjectBreakdown.useQuery(filters, {
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: shouldRetryError,
  });
};

/**
 * Hook to fetch top surveys by response count
 *
 * Returns array of top-performing surveys with response counts
 * and completion rates.
 *
 * @param filters - Analytics filter options
 * @param limit - Maximum number of surveys to return (default: 10)
 * @param enabled - Whether query is enabled (default: true)
 */
export const useInsightTopSurveys = (
  filters: InsightFilterOptions,
  limit: number = 10,
  enabled: boolean = true
) => {
  return trpc.insight.getTopSurveys.useQuery(
    { ...filters, limit },
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: shouldRetryError,
    }
  );
};

/**
 * Hook to fetch completion metrics
 *
 * Returns response completion statistics including completion rate,
 * partial responses, and duration metrics.
 *
 * @param filters - Analytics filter options
 * @param enabled - Whether query is enabled (default: true)
 */
export const useInsightCompletionMetrics = (
  filters: InsightFilterOptions,
  enabled: boolean = true
) => {
  return trpc.insight.getCompletionMetrics.useQuery(filters, {
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: shouldRetryError,
  });
};

/**
 * Hook to fetch complete analytics overview
 *
 * Convenience hook that fetches all analytics data in parallel:
 * - Summary statistics
 * - Recent activity time series
 * - Domain breakdown
 * - Top surveys
 * - Completion metrics
 *
 * Use this for dashboard overview pages to minimize API calls.
 *
 * @param filters - Analytics filter options
 * @param options - Additional options for overview query
 * @param enabled - Whether query is enabled (default: true)
 */
export const useInsightOverview = (
  filters: InsightFilterOptions,
  options?: {
    timeSeriesLimit?: number;
    topSurveysLimit?: number;
  },
  enabled: boolean = true
) => {
  return trpc.insight.getOverview.useQuery(
    {
      ...filters,
      timeSeriesLimit: options?.timeSeriesLimit ?? 30,
      topSurveysLimit: options?.topSurveysLimit ?? 10,
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: shouldRetryError,
      placeholderData: keepPreviousData,
    }
  );
};

/**
 * Hook to fetch question answer distribution
 *
 * Returns answer value counts and optional numeric stats for a specific question.
 *
 * @param filters - Analytics filter options
 * @param questionId - Question ID to get distribution for
 * @param enabled - Whether query is enabled (default: true)
 */
export const useQuestionDistribution = (
  filters: InsightFilterOptions,
  questionId: string,
  enabled: boolean = true
) => {
  return trpc.insight.getQuestionDistribution.useQuery(
    { ...filters, questionId },
    {
      enabled: enabled && !!questionId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: shouldRetryError,
    }
  );
};

/**
 * Hook to fetch question answer timeline
 *
 * Returns answer value counts grouped by time period for trend analysis.
 *
 * @param filters - Analytics filter options
 * @param questionId - Question ID to get timeline for
 * @param granularity - Time period granularity
 * @param enabled - Whether query is enabled (default: true)
 */
export const useQuestionTimeline = (
  filters: InsightFilterOptions,
  questionId: string,
  granularity: 'day' | 'week' | 'month' = 'week',
  enabled: boolean = true
) => {
  return trpc.insight.getQuestionTimeline.useQuery(
    { ...filters, questionId, granularity },
    {
      enabled: enabled && !!questionId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: shouldRetryError,
    }
  );
};

/**
 * Hook to fetch available questions from answers data
 *
 * Returns distinct questions that have answer data, with counts and types.
 *
 * @param filters - Analytics filter options
 * @param surveyId - Optional survey filter
 * @param enabled - Whether query is enabled (default: true)
 */
export const useAvailableQuestions = (
  filters: InsightFilterOptions,
  surveyId?: string,
  enabled: boolean = true
) => {
  return trpc.insight.getAvailableQuestions.useQuery(
    { ...filters, surveyId },
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: shouldRetryError,
    }
  );
};

/**
 * Hook to fetch answer summary statistics
 *
 * Returns aggregate statistics from the answers table.
 *
 * @param filters - Analytics filter options
 * @param enabled - Whether query is enabled (default: true)
 */
export const useAnswerSummary = (filters: InsightFilterOptions, enabled: boolean = true) => {
  return trpc.insight.getAnswerSummary.useQuery(filters, {
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: shouldRetryError,
  });
};
